import { MenuHeader } from './menu-header/menu-header';
import { MenuUtilities } from './menu-utilities/menu-utilities';
import { PrimaryMenu } from './primary-menu/primary-menu';
import { SubLevelMenu } from './primary-menu/sub-level-menu/sub-level-menu';
import type { MenuItem, MenuProps } from '../menu-types';
import { createSignal, onCleanup, onMount, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { TabHandler } from '../../../tools/tab-handler';
import { Section } from '../../../grid-system/section/section';
import { AppContext } from '../../../app-context-provider/app-context-provider';

/* Slide duration — must match the `duration-300` class on the slide track. */
const SLIDE_DURATION_MS = 300;

export const MenuMobile = (props: MenuProps) => {
    const { appStore } = useContext(AppContext);
    const { close } = appStore;
    const [firstTabbableElement, setFirstTabbableElement] = createSignal<HTMLElement | undefined>();
    const [lastTabbableElement] = createSignal<HTMLElement | undefined>();
    const [activeParent, setActiveParent] = createSignal<MenuItem | null>(null);
    const [renderParent, setRenderParent] = createSignal<MenuItem | null>(null);

    /* Captures whichever element triggered the drill-in (typically the parent
       row's chevron button) so we can return focus to it on pop. WAI-ARIA
       tree-view convention: collapsing returns focus to the trigger. Falls
       back to firstTabbableElement if the trigger isn't tracked. */
    let drillTrigger: HTMLElement | null = null;

    const handleSetActiveParent = (parent: MenuItem | null) => {
        if (parent) {
            drillTrigger = document.activeElement as HTMLElement | null;
            setRenderParent(parent);
            setActiveParent(parent);
        } else {
            setActiveParent(null);
            /* rAF waits for the re-render so the root pane is no longer inert
               and the trigger element is back in the active tab order. */
            requestAnimationFrame(() => {
                (drillTrigger ?? firstTabbableElement())?.focus();
                drillTrigger = null;
            });
            setTimeout(() => {
                if (!activeParent()) setRenderParent(null);
            }, SLIDE_DURATION_MS);
        }
    };

    const isDrilled = () => Boolean(activeParent());

    /* ESC behaviour: when drilled into a sub-view, pop one level back to root.
       When at root, do nothing — the existing document-level ESC handler in
       `menu.tsx` closes the whole overlay during bubble phase. We capture
       in the capture phase (third arg `true`) so we can stopImmediatePropagation
       on document and prevent menu.tsx's bubble-phase listener from also firing. */
    onMount(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (activeParent()) {
                e.stopImmediatePropagation();
                handleSetActiveParent(null);
            }
        };
        document.addEventListener('keydown', handleEscape, true);
        onCleanup(() => document.removeEventListener('keydown', handleEscape, true));
    });

    /* Slide wrapper lives OUTSIDE the MenuHeader's Section so it can be viewport-
       wide with its own `overflow-hidden` — clips the off-screen pane at the
       viewport edge without affecting the sub-view's full-bleed context-strip
       background (which now lives naturally inside a 100vw-wide pane). Each pane
       wraps its own Section to keep menu rows aligned with the site grid. */
    return (
        <TabHandler refs={{ firstTabbableElement, lastTabbableElement }}>
            <div class="flex flex-col bg-cvi-bg-body">
                <Section
                    templateShorthand={[12]}
                    widthType="bgFull"
                    removeSpacingBlock={true}
                    customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                >
                    <MenuHeader
                        handleCloseMenuClick={props?.handleCloseMenuClick}
                        firstElement={setFirstTabbableElement}
                        activeParent={activeParent}
                        onBack={() => handleSetActiveParent(null)}
                    />
                </Section>

                <div class="overflow-hidden">
                    <div
                        class={clsx(
                            'flex items-start w-[200%] transition-transform duration-300 ease-out',
                            isDrilled() ? '-translate-x-1/2' : 'translate-x-0'
                        )}
                    >
                        <div class="w-1/2 shrink-0" inert={isDrilled() || undefined}>
                            <div
                                class="h-px bg-cvi-border-muted"
                                style="margin-left: calc(50% - 50vw); margin-right: calc(50% - 50vw);"
                            />
                            <Section
                                templateShorthand={[12]}
                                widthType="bgFull"
                                removeSpacingBlock={true}
                                customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                            >
                                <PrimaryMenu
                                    menu={props?.primaryMenuLinks}
                                    setActiveParent={handleSetActiveParent}
                                />
                            </Section>
                            <Section
                                templateShorthand={[12]}
                                widthType="bgFull"
                                removeSpacingBlock={true}
                                customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                            >
                                <MenuUtilities
                                    headerLinks={props?.headerLinks}
                                    utilityLinks={props?.utilityLinks}
                                    onLinkClick={close}
                                />
                            </Section>
                        </div>
                        <div class="w-1/2 shrink-0" inert={!isDrilled() || undefined}>
                            <Show when={renderParent()}>
                                <SubLevelMenu parent={renderParent()!} onLinkClick={close} />
                            </Show>
                        </div>
                    </div>
                </div>
            </div>
        </TabHandler>
    );
};
